import {call, put, takeLatest} from "redux-saga/effects";
import {PayloadAction} from "@reduxjs/toolkit";
import {BillingApi} from "../../../API/ApiClasses/BillingApi";
import {
  BillingApiTariffRequest,
  BillingApiTariffResponse,
  BillingApiUpdateTrialEndDateRequest,
  BillingApiUpdateTrialEndDateResponse,
  BillingApiSubscriptionCancelRequest,
  BillingApiSubscriptionCancelResponse,
  BillingApiSubscriptionResumeRequest,
  BillingApiSubscriptionResumeResponse,
  BillingApiSetupIntentRequest,
  BillingApiSetupIntentResponse,
  BillingApiAttachPaymentMethodRequest,
  BillingApiAttachPaymentMethodResponse,
  BillingApiSubscriptionPricesResponse,
  BillingApiUpdateSubscriptionQuantityRequest,
  BillingApiUpdateSubscriptionQuantityResponse,
  EmptyRequest,
} from "../../../proto/generated/api_entities_pb";
import {
  getTariff,
  setTariffResult,
  getSubscriptionPrices,
  setSubscriptionPricesResult,
  updateTrialEndDate,
  setUpdateTrialEndDateResult,
  cancelSubscription,
  setCancelSubscriptionResult,
  resumeSubscription,
  setResumeSubscriptionResult,
  createSetupIntent,
  setCreateSetupIntentResult,
  attachPaymentMethod,
  setAttachPaymentMethodResult,
  updateSubscriptionQuantity,
  setUpdateSubscriptionQuantityResult,
  refreshInvoiceList,
} from "../../reducers/tariffReducer/tariffReducer";
import {
  IGetTariffPayload,
  IGetSubscriptionPricesPayload,
  IUpdateTrialEndDatePayload,
  ICancelSubscriptionPayload,
  IResumeSubscriptionPayload,
  ICreateSetupIntentPayload,
  IAttachPaymentMethodPayload,
  IUpdateSubscriptionQuantityPayload,
} from "../../reducers/tariffReducer/tariffReducerTypes";
import {getErrorMessageByCode} from "../../../utils/constans/errorCodes/errorCodes";
import {Logger} from "../../../utils/logger/Logger";

function* getTariffSaga({payload}: PayloadAction<IGetTariffPayload>) {
  const req = new BillingApiTariffRequest();

  try {
    const res: BillingApiTariffResponse = yield call(BillingApi.getTariff, req);

    if (res.getResultresponse()?.getIssuccessful()) {
      yield put(setTariffResult({
        item: res.getTariff(),
      }));
    } else {
      yield put(setTariffResult({
        errorMessage: getErrorMessageByCode(res.getResultresponse()?.getErrormessage()),
      }));
    }
  } catch (e: any) {
    const errorMsg = getErrorMessageByCode(e);
    Logger.error(errorMsg);
    yield put(setTariffResult({
      errorMessage: errorMsg,
    }));
  }
}

function* updateTrialEndDateSaga({payload}: PayloadAction<IUpdateTrialEndDatePayload>) {
  const req = new BillingApiUpdateTrialEndDateRequest()
    .setUserid(payload.userId)
    .setTariffend(payload.tariffEnd);

  try {
    const res: BillingApiUpdateTrialEndDateResponse = yield call(BillingApi.updateTrialEndDate, req);

    if (res.getResultresponse()?.getIssuccessful()) {
      yield put(setUpdateTrialEndDateResult({}));
    } else {
      yield put(setUpdateTrialEndDateResult({
        errorMessage: getErrorMessageByCode(res.getResultresponse()?.getErrormessage()),
      }));
    }
  } catch (e: any) {
    const errorMsg = getErrorMessageByCode(e);
    Logger.error(errorMsg);
    yield put(setUpdateTrialEndDateResult({
      errorMessage: errorMsg,
    }));
  }
}

function* cancelSubscriptionSaga({payload}: PayloadAction<ICancelSubscriptionPayload>) {
  const req = new BillingApiSubscriptionCancelRequest()
    .setCompanytariffpackageid(payload.companyTariffPackageId);

  try {
    const res: BillingApiSubscriptionCancelResponse = yield call(BillingApi.cancelSubscription, req);

    if (res.getResultresponse()?.getIssuccessful()) {
      yield put(setCancelSubscriptionResult({}));
      // Refresh tariff and invoice list after cancellation
      yield put(getTariff({silent: true}));
      yield put(refreshInvoiceList());
    } else {
      yield put(setCancelSubscriptionResult({
        errorMessage: getErrorMessageByCode(res.getResultresponse()?.getErrormessage()),
      }));
    }
  } catch (e: any) {
    const errorMsg = getErrorMessageByCode(e);
    Logger.error(errorMsg);
    yield put(setCancelSubscriptionResult({
      errorMessage: errorMsg,
    }));
  }
}

function* resumeSubscriptionSaga({payload}: PayloadAction<IResumeSubscriptionPayload>) {
  const req = new BillingApiSubscriptionResumeRequest()
    .setCompanytariffpackageid(payload.companyTariffPackageId);

  try {
    const res: BillingApiSubscriptionResumeResponse = yield call(BillingApi.resumeSubscription, req);

    if (res.getResultresponse()?.getIssuccessful()) {
      yield put(setResumeSubscriptionResult({}));
      // Refresh tariff and invoice list after resuming
      yield put(getTariff({silent: true}));
      yield put(refreshInvoiceList());
    } else {
      yield put(setResumeSubscriptionResult({
        errorMessage: getErrorMessageByCode(res.getResultresponse()?.getErrormessage()),
      }));
    }
  } catch (e: any) {
    const errorMsg = getErrorMessageByCode(e);
    Logger.error(errorMsg);
    yield put(setResumeSubscriptionResult({
      errorMessage: errorMsg,
    }));
  }
}

function* createSetupIntentSaga({payload}: PayloadAction<ICreateSetupIntentPayload>) {
  const req = new BillingApiSetupIntentRequest();

  try {
    const res: BillingApiSetupIntentResponse = yield call(BillingApi.createSetupIntent, req);

    if (res.getResultresponse()?.getIssuccessful()) {
      yield put(setCreateSetupIntentResult({
        clientSecret: res.getClientsecret() || undefined,
      }));
    } else {
      yield put(setCreateSetupIntentResult({
        errorMessage: getErrorMessageByCode(res.getResultresponse()?.getErrormessage()),
      }));
    }
  } catch (e: any) {
    const errorMsg = getErrorMessageByCode(e);
    Logger.error(errorMsg);
    yield put(setCreateSetupIntentResult({
      errorMessage: errorMsg,
    }));
  }
}

function* attachPaymentMethodSaga({payload}: PayloadAction<IAttachPaymentMethodPayload>) {
  const req = new BillingApiAttachPaymentMethodRequest()
    .setPaymentmethodid(payload.paymentMethodId);

  try {
    const res: BillingApiAttachPaymentMethodResponse = yield call(BillingApi.attachPaymentMethod, req);
    const resultResponse = res.getResultresponse();
    const isSuccess = resultResponse?.getIssuccessful() === true;

    if (isSuccess) {
      yield put(setAttachPaymentMethodResult({}));
      yield put(getTariff({silent: true}));
      yield put(refreshInvoiceList());
    } else {
      const errMsg = resultResponse?.getErrormessage() ?? '';
      yield put(setAttachPaymentMethodResult({
        errorMessage: getErrorMessageByCode(errMsg) || 'Failed to add payment method',
      }));
    }
  } catch (e: any) {
    const errorMsg = getErrorMessageByCode(e);
    Logger.error(errorMsg);
    yield put(setAttachPaymentMethodResult({
      errorMessage: errorMsg || 'Failed to add payment method',
    }));
  }
}

function* getSubscriptionPricesSaga({payload}: PayloadAction<IGetSubscriptionPricesPayload>) {
  const req = new EmptyRequest();
  try {
    const res: BillingApiSubscriptionPricesResponse = yield call(BillingApi.getSubscriptionPrices, req);
    const raw = res as any;
    const pricePerSeatCents = typeof raw.getPriceperseatcents === 'function' ? raw.getPriceperseatcents() : raw.getPricePerSeatCents?.() ?? 0;
    const pricePerAircraftCents = typeof raw.getPriceperaircraftcents === 'function' ? raw.getPriceperaircraftcents() : raw.getPricePerAircraftCents?.() ?? 0;
    const currency = (typeof raw.getCurrency === 'function' ? raw.getCurrency() : raw.getCurrency?.()) ?? 'usd';
    const discountPercent = typeof raw.getDiscountpercent === 'function' ? raw.getDiscountpercent() : raw.getDiscountPercent?.();
    const discountDurationMonths = typeof raw.getDiscountdurationmonths === 'function' ? raw.getDiscountdurationmonths() : raw.getDiscountDurationMonths?.();
    const discountEndsAt = typeof raw.getDiscountendsat === 'function' ? raw.getDiscountendsat() : raw.getDiscountEndsAt?.();
    yield put(setSubscriptionPricesResult({
      pricePerSeatCents,
      pricePerAircraftCents,
      currency,
      discountPercent: discountPercent ?? undefined,
      discountDurationMonths: discountDurationMonths ?? undefined,
      discountEndsAt: discountEndsAt || undefined,
    }));
  } catch (e: any) {
    const errorMsg = getErrorMessageByCode(e);
    Logger.error(errorMsg);
    yield put(setSubscriptionPricesResult({
      errorMessage: errorMsg,
    }));
  }
}

function* updateSubscriptionQuantitySaga({payload}: PayloadAction<IUpdateSubscriptionQuantityPayload>) {
  const req = new BillingApiUpdateSubscriptionQuantityRequest()
    .setCompanytariffpackageid(payload.companyTariffPackageId)
    .setUsersquantity(payload.usersQuantity)
    .setAircraftquantity(payload.aircraftQuantity);

  try {
    const res: BillingApiUpdateSubscriptionQuantityResponse = yield call(BillingApi.updateSubscriptionQuantity, req);

    if (res.getResultresponse()?.getIssuccessful()) {
      yield put(setUpdateSubscriptionQuantityResult({}));
      yield put(getTariff({silent: true}));
      yield put(refreshInvoiceList());
    } else {
      yield put(setUpdateSubscriptionQuantityResult({
        errorMessage: getErrorMessageByCode(res.getResultresponse()?.getErrormessage()),
      }));
    }
  } catch (e: any) {
    const errorMsg = getErrorMessageByCode(e);
    Logger.error(errorMsg);
    yield put(setUpdateSubscriptionQuantityResult({
      errorMessage: errorMsg,
    }));
  }
}

export function* tariffSaga() {
  yield takeLatest(getTariff.type, getTariffSaga);
  yield takeLatest(getSubscriptionPrices.type, getSubscriptionPricesSaga);
  yield takeLatest(updateTrialEndDate.type, updateTrialEndDateSaga);
  yield takeLatest(cancelSubscription.type, cancelSubscriptionSaga);
  yield takeLatest(resumeSubscription.type, resumeSubscriptionSaga);
  yield takeLatest(createSetupIntent.type, createSetupIntentSaga);
  yield takeLatest(attachPaymentMethod.type, attachPaymentMethodSaga);
  yield takeLatest(updateSubscriptionQuantity.type, updateSubscriptionQuantitySaga);
}

